// Heading — the semantic titling primitive, pure.
//
// Rebuilt from foundations v1 against the design vision (.claude/research/design/06).
// Two axes are deliberately DECOUPLED: `level` is the document's semantic
// rank (renders h1…h4, so the heading OUTLINE stays correct for AT and SC
// 1.3.1 / 2.4.6), while `size` is the visual weight on the page. A section
// that must sit lower in the outline can still read large, and vice versa —
// so authors never reach for the wrong tag to get the right size.
//
// Distinct props per axis (no flag overloading): `level` picks the tag,
// `size` picks the type scale, `weight` and `tone` are independent knobs.
// Each is a small named union, not a boolean whose value swaps meaning.
//
// Near-monochrome register: `tone` spans only the neutral ink ramp
// (fg / fg-dim / fg-faint) — the accent is reserved for the primary action
// and the agent's identity (.claude/research/design/06 §1), never spent on a title.
//
// Pure/props-driven: no side effects, no domain knowledge. `size` defaults
// sensibly from `level` (h1 → 3xl … h4 → lg) so the common case needs one
// prop; every other heading attribute (id, aria-*, onClick…) passes through.
// The material title is `level={1} size="3xl"` (--text-3xl, the canvas
// anchor). Font FACE is inherited, so a heading adopts the reading face
// inside the material canvas and sans in chrome — the context decides.

import type { HTMLAttributes } from "react";
import "./Heading.css";

// Semantic rank — drives the rendered tag (h1…h4), i.e. the outline position.
type HeadingLevel = 1 | 2 | 3 | 4;
// Visual size — the type scale, decoupled from rank. Maps to --text-*.
type HeadingSize = "lg" | "xl" | "2xl" | "3xl";
export type HeadingWeight = "regular" | "medium" | "semibold";
// Neutral ink ramp only — colour never carries the heading's meaning.
type HeadingTone = "default" | "dim" | "faint";

export type HeadingProps = HTMLAttributes<HTMLHeadingElement> & {
    readonly level: HeadingLevel;
    readonly size?: HeadingSize;
    readonly weight?: HeadingWeight;
    readonly tone?: HeadingTone;
};

// Sensible visual default per rank, overridable via `size` (the decoupling).
const defaultSizeForLevel: Record<HeadingLevel, HeadingSize> = {
    1: "3xl",
    2: "2xl",
    3: "xl",
    4: "lg",
};

export const Heading = ({
    level,
    size,
    weight = "semibold",
    tone = "default",
    className,
    ...rest
}: HeadingProps) => {
    // Template-literal type infers "h1" | … | "h4" — a valid host tag, no cast.
    const Tag: `h${HeadingLevel}` = `h${level}`;
    const visualSize = size ?? defaultSizeForLevel[level];
    return (
        <Tag
            className={`bilda-heading bilda-heading--${visualSize} bilda-heading--${weight} bilda-heading--${tone}${
                className ? ` ${className}` : ""
            }`}
            {...rest}
        />
    );
};
